// Coordinate conversion utilities for Go board

import type { Point } from '../types/game';

/**
 * Round a KataGo scoreLead to a displayable value, matching KataGUI's convention:
 * - Whole komi (e.g. 7): round to nearest integer
 * - x.5 komi (e.g. 6.5): floor then add 0.5  (2.1 → 2.5, 2.9 → 2.5)
 * komi defaults to 6.5 if omitted.
 */
export function roundScore(score: number, komi = 6.5): number {
  if (komi === Math.floor(komi)) {
    return Math.round(score);
  } else {
    return Math.sign(score) * (Math.floor(Math.abs(score)) + 0.5);
  }
}

/** Format a score for display, e.g. "+2.5" or "-3.0". */
export function formatScore(score: number, komi = 6.5): string {
  const r = roundScore(score, komi);
  return (r >= 0 ? '+' : '') + Math.abs(r).toFixed(komi === Math.floor(komi) ? 0 : 1);
}

// SGF coordinate system: A-T (skip I), 1-19
// A=1, B=2, C=3, ..., H=8, J=9 (skip I), K=10, ..., T=19
const SGF_LETTERS = 'ABCDEFGHJKLMNOPQRST';

/**
 * Convert SGF move notation (e.g., 'Q16') to board coordinates (0-indexed)
 */
export function sgfToPoint(sgfMove: string, boardSize = 19): Point | null {
  if (!sgfMove || sgfMove === 'pass' || sgfMove === 'resign') {
    return null;
  }

  const col = SGF_LETTERS.indexOf(sgfMove[0].toUpperCase());
  const row = boardSize - parseInt(sgfMove.slice(1), 10); // SGF rows are bottom-up

  if (col === -1 || isNaN(row) || row < 0 || row >= boardSize || col >= boardSize) {
    return null;
  }

  return { row, col };
}

/**
 * Convert board coordinates (0-indexed) to SGF move notation (e.g., 'Q16')
 */
export function pointToSGF(point: Point, boardSize = 19): string {
  if (!point || point.row < 0 || point.row >= boardSize || point.col < 0 || point.col >= boardSize) {
    return '';
  }

  const col = SGF_LETTERS[point.col];
  const row = boardSize - point.row;

  return `${col}${row}`;
}

/**
 * Check if a point is within board bounds
 */
export function isValidPoint(point: Point, boardSize = 19): boolean {
  return point.row >= 0 && point.row < boardSize && point.col >= 0 && point.col < boardSize;
}

/**
 * Get neighboring points (up, down, left, right)
 */
export function getNeighbors(point: Point, boardSize = 19): Point[] {
  const neighbors: Point[] = [];
  const deltas = [
    { row: -1, col: 0 },
    { row: 1, col: 0 },
    { row: 0, col: -1 },
    { row: 0, col: 1 },
  ];

  for (const delta of deltas) {
    const neighbor = { row: point.row + delta.row, col: point.col + delta.col };
    if (isValidPoint(neighbor, boardSize)) {
      neighbors.push(neighbor);
    }
  }

  return neighbors;
}

/**
 * Get all points on the board
 */
export function getAllPoints(boardSize = 19): Point[] {
  const points: Point[] = [];
  for (let row = 0; row < boardSize; row++) {
    for (let col = 0; col < boardSize; col++) {
      points.push({ row, col });
    }
  }
  return points;
}

/**
 * Calculate the coordinate for placing handicap stones
 */
export function getHandicapPoints(boardSize: number, handicap: number): Point[] {
  if (handicap < 2 || handicap > 9) return [];

  const e = boardSize >= 13 ? 3 : 2; // distance from edge (0-indexed)
  const m = Math.floor(boardSize / 2);
  const n = boardSize - 1 - e;

  // Named star points
  const TR = { row: e, col: n }; // top-right    (Q16)
  const BL = { row: n, col: e }; // bottom-left  (D4)
  const BR = { row: n, col: n }; // bottom-right (Q4)
  const TL = { row: e, col: e }; // top-left     (D16)
  const L  = { row: m, col: e }; // left         (D10)
  const R  = { row: m, col: n }; // right        (Q10)
  const T  = { row: e, col: m }; // top          (K16)
  const B  = { row: n, col: m }; // bottom       (K4)
  const C  = { row: m, col: m }; // center       (K10)

  // Standard Japanese/AGA handicap patterns
  const patterns: Record<number, Point[]> = {
    2: [TR, BL],
    3: [TR, BL, BR],
    4: [TR, BL, BR, TL],
    5: [TR, BL, BR, TL, C],
    6: [TR, BL, BR, TL, L, R],
    7: [TR, BL, BR, TL, L, R, C],
    8: [TR, BL, BR, TL, L, R, T, B],
    9: [TR, BL, BR, TL, L, R, T, B, C],
  };

  return patterns[handicap] ?? [];
}

/**
 * Convert pixel coordinates to board coordinates (for click handling)
 */
export function pixelToPoint(x: number, y: number, cellSize: number, boardSize = 19): Point | null {
  const col = Math.round(x / cellSize);
  const row = Math.round(y / cellSize);

  const point = { row, col };
  return isValidPoint(point, boardSize) ? point : null;
}

/**
 * Convert board coordinates to pixel coordinates (center of intersection)
 */
export function pointToPixel(point: Point, cellSize: number): { x: number; y: number } {
  return {
    x: point.col * cellSize,
    y: point.row * cellSize,
  };
}
